import { useState, useCallback, useMemo } from "react";
import { Driver } from "../types/driver.types";
import { DriverService } from "../service/DriverService";
import { AuthService } from "../service/AuthService";

export const useDriverApi = () => {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const driverService = useMemo(() => new DriverService(), []);
  const authService = useMemo(() => new AuthService(), []);

  const handleError = useCallback((err: unknown) => {
    const errorMessage =
      err instanceof Error ? err.message : "An error occurred";
    console.error("API Error:", errorMessage, err);
    setError(errorMessage);
    throw new Error(errorMessage);
  }, []);

  const getTransporterId = useCallback(() => {
    const user = authService.getUser();
    if (!user || !user._id) {
      throw new Error("User not authenticated");
    }
    return user._id;
  }, [authService]);

  const fetchDrivers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const transporterId = getTransporterId();
      const data = await driverService.getDrivers(transporterId);
      setDrivers(data);
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  }, [getTransporterId, driverService, handleError]);

  const addDriver = useCallback(
    async (driverData: Omit<Driver, "_id" | "password">) => {
      setIsLoading(true);
      setError(null);
      try {
        const newDriver = await driverService.addDriver(
          getTransporterId(),
          driverData
        );
        setDrivers((prevDrivers) => [...prevDrivers, newDriver]);
        return newDriver;
      } catch (err:any) {
          setError(err.message);
          handleError(err);
      } finally {
        setIsLoading(false);
      }
    },
    [driverService, getTransporterId, handleError]
  );

  const updateDriver = useCallback(
    async (driverId: string, driverData: Partial<Driver>) => {
      setIsLoading(true);
      setError(null);
      try {
        // console.log("Updating driver:", driverId, driverData);
        const updatedDriver = await driverService.updateDriver(
          driverId,
          driverData
        );
        // console.log("Driver updated successfully:", updatedDriver);
        setDrivers((prevDrivers) => {
          const updatedDrivers = prevDrivers.map((d) =>
            d._id === driverId ? updatedDriver : d
          );
          // console.log("Updated drivers state:", updatedDrivers);
          return updatedDrivers;
        });
        return updatedDriver;
      } catch (err) {
        console.error("Error updating driver:", err);
        handleError(err);
      } finally {
        setIsLoading(false);
      }
    },
    [driverService, handleError]
  );

  const deleteDriver = useCallback(
    async (driverId: string | undefined) => {
      if (!driverId) {
        console.error("Attempted to delete driver with undefined id");
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        await driverService.deleteDriver(driverId, getTransporterId());
        setDrivers((prevDrivers) =>
          prevDrivers.filter((d) => d._id !== driverId)
        );
      } catch (err) {
        handleError(err);
      } finally {
        setIsLoading(false);
      }
    },
    [driverService, getTransporterId, handleError]
  );

  const resetDriverPassword = useCallback(
    async (driverId: string) => {
      setIsLoading(true);
      setError(null);
      try {
        await driverService.resetDriverPassword(driverId);
        // console.log("Driver password reset successfully");
      } catch (err) {
        console.error("Error resetting driver password:", err);
        handleError(err);
      } finally {
        setIsLoading(false);
      }
    },
    [driverService, handleError]
  );

  return {
    drivers,
    isLoading,
    error,
    fetchDrivers,
    addDriver,
    updateDriver,
    deleteDriver,
    resetDriverPassword,
  };
};
